<div class="overview-container">

  @if (IsInitializing) {
    <div class="loading-overlay"><div class="spinner"></div> Loading...</div>
  }

  @if (!IsInitializing && !IsAuthorizedUser) {
    <div class="empty-state">
      <i class="fa-solid fa-lock"></i>
      <p>Access restricted. Only system users or users with the Integration role can view this page.</p>
    </div>
  }

  @if (!IsInitializing && IsAuthorizedUser) {

    <!-- Header with Time Preset Selector -->
    <div class="overview-header">
      <div class="header-title-area">
        <div class="header-icon"><i class="fa-solid fa-gauge-high"></i></div>
        <div>
          <h1 class="header-title">System Overview</h1>
          <p class="header-subtitle">Cross-organization KPIs and activity</p>
        </div>
      </div>
      <div class="header-actions">
        <div class="time-presets">
          @for (preset of TimePresets; track preset) {
            <button
              class="preset-btn"
              [class.preset-active]="TimePreset === preset"
              (click)="SetTimePreset(preset)">
              {{ GetPresetLabel(preset) }}
            </button>
          }
        </div>
        @if (TimePreset === 'custom') {
          <div class="custom-range">
            <input type="date" [(ngModel)]="CustomStartDate" />
            <span class="custom-range-sep">to</span>
            <input type="date" [(ngModel)]="CustomEndDate" />
            <button class="btn-apply" (click)="ApplyCustomRange()">Apply</button>
          </div>
        }
        <button class="btn-refresh" (click)="LoadAllData()" [disabled]="Loading">
          <i class="fa-solid fa-arrows-rotate" [class.fa-spin]="Loading"></i>
        </button>
      </div>
    </div>

    <!-- Error Banner -->
    @if (Error) {
      <div class="error-banner">
        <i class="fa-solid fa-circle-exclamation"></i>
        {{ Error }}
        <button (click)="Error = null">&times;</button>
      </div>
    }

    <!-- Loading State -->
    @if (Loading && !Stats) {
      <div class="loading-overlay"><div class="spinner"></div> Loading overview data...</div>
    }

    <!-- KPI Cards Row -->
    @if (Stats) {
      <div class="kpi-row">
        <div class="kpi-card">
          <span class="kpi-value">{{ Stats.totalConversations }}</span>
          <span class="kpi-label">Total Conversations</span>
        </div>

        <div class="kpi-card">
          <span class="kpi-value">{{ Stats.activeOrganizations }}
            <span class="kpi-sub">of {{ Stats.totalOrganizations }}</span>
          </span>
          <span class="kpi-label">Active Organizations</span>
        </div>

        <div class="kpi-card">
          <span class="kpi-value">{{ Stats.totalAgentRuns }}</span>
          <span class="kpi-label">Total Agent Runs</span>
        </div>

        <div class="kpi-card">
          <span class="kpi-value" [class]="GetSuccessRateClass()">{{ Stats.successRate.toFixed(1) }}%</span>
          <span class="kpi-label">Success Rate</span>
        </div>

        <div class="kpi-card">
          <span class="kpi-value kpi-accent">{{ FormatResponseTime(Stats.avgResponseTimeMs) }}</span>
          <span class="kpi-label">Avg Response Time</span>
        </div>

        <div class="kpi-card">
          <span class="kpi-value kpi-cost">{{ FormatCost(Stats.totalCost) }}</span>
          <span class="kpi-label">Total Cost</span>
        </div>
      </div>
    }

    <!-- Organization Activity Grid -->
    @if (OrgActivities.length > 0) {
      <div class="section-header-row">
        <h2 class="section-title"><i class="fa-solid fa-building"></i> Organization Activity</h2>
        <span class="section-badge">{{ OrgActivities.length }} organizations</span>
      </div>

      <div class="org-grid">
        @for (org of OrgActivities; track org.organizationID) {
          <div class="org-card" [ngClass]="GetOrgActivityClass(org)">
            <div class="org-card-header">
              <span class="org-name">{{ org.organizationName }}</span>
              <span class="org-last-active">{{ FormatRelativeTime(org.lastActivityAt) }}</span>
            </div>
            <div class="org-card-stats">
              <span class="org-stat">
                <i class="fa-solid fa-comments"></i> {{ org.conversationCount }}
              </span>
              <span class="org-stat">
                <i class="fa-solid fa-users"></i> {{ org.activeUsers }}
              </span>
            </div>
            <div class="org-activity-bar-wrap">
              <div class="org-activity-bar" [style.width.%]="GetOrgActivityPercent(org)"></div>
            </div>
          </div>
        }
      </div>
    }

    <!-- Bottom Two-Column Section -->
    <div class="bottom-grid">
      <!-- Recent Failures -->
      <div class="panel">
        <div class="panel-header">
          <h3 class="panel-title"><i class="fa-solid fa-triangle-exclamation"></i> Recent Failures</h3>
        </div>
        @if (RecentFailures.length > 0) {
          <div class="failures-list">
            @for (failure of RecentFailures; track failure.agentRunID) {
              <div class="failure-row">
                <div class="failure-info">
                  <span class="failure-org">{{ failure.organizationName }}</span>
                  <span class="failure-person">{{ failure.personName }}</span>
                </div>
                <div class="failure-error">{{ TruncateError(failure.errorMessage) }}</div>
                <div class="failure-time">{{ FormatDate(failure.startedAt) }}</div>
              </div>
            }
          </div>
        } @else {
          <div class="empty-state-sm">
            <i class="fa-solid fa-check-circle"></i>
            <p>No recent failures</p>
          </div>
        }
      </div>

      <!-- Activity Trend -->
      <div class="panel">
        <div class="panel-header">
          <h3 class="panel-title"><i class="fa-solid fa-chart-bar"></i> Activity Trend</h3>
        </div>
        @if (DailyTrends.length > 0) {
          <div class="trend-chart">
            <div class="trend-bars">
              @for (trend of DailyTrends; track trend.day) {
                <div class="trend-col" [title]="FormatShortDay(trend.day) + ': ' + trend.conversationCount + ' conversations, ' + trend.failedCount + ' failed'">
                  <div class="trend-bar-stack">
                    <div class="trend-bar" [style.height.%]="GetTrendBarHeight(trend)"></div>
                    @if (trend.failedCount > 0) {
                      <div class="trend-bar-failed" [style.height.%]="GetTrendBarFailedHeight(trend)"></div>
                    }
                  </div>
                  <span class="trend-label">{{ FormatShortDay(trend.day) }}</span>
                </div>
              }
            </div>
            <div class="trend-legend">
              <span class="legend-item"><span class="legend-dot legend-dot-conversations"></span> Conversations</span>
              <span class="legend-item"><span class="legend-dot legend-dot-failed"></span> Failed</span>
            </div>
          </div>
        } @else {
          <div class="empty-state-sm">
            <i class="fa-solid fa-chart-bar"></i>
            <p>No trend data for this period</p>
          </div>
        }
      </div>
    </div>

  }
</div>
